import { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import {
  QuoteReminderTemplate,
  QuoteReminderTemplateUpdate,
} from "@/types/quoteReminderTemplates";

export function useQuoteReminderTemplates() {
  const [templates, setTemplates] = useState<QuoteReminderTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from("quote_reminder_templates")
        .select("*")
        .eq("is_active", true)
        .order("step", { ascending: true });

      if (fetchError) throw fetchError;
      setTemplates((data as QuoteReminderTemplate[]) || []);
    } catch (err: any) {
      console.error("Erreur chargement templates relances:", err);
      setError(err.message || "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();

    const channel = supabase
      .channel("quote_reminder_templates_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "quote_reminder_templates",
        },
        (payload) => {
          if (payload.eventType === "INSERT") {
            setTemplates((prev) =>
              [...prev, payload.new as QuoteReminderTemplate].sort((a, b) => a.step - b.step)
            );
          } else if (payload.eventType === "UPDATE") {
            setTemplates((prev) =>
              prev
                .map((template) =>
                  template.id === payload.new.id ? (payload.new as QuoteReminderTemplate) : template
                )
                .filter((template) => template.is_active)
                .sort((a, b) => a.step - b.step)
            );
          } else if (payload.eventType === "DELETE") {
            setTemplates((prev) => prev.filter((template) => template.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const updateTemplate = async (
    id: string,
    updates: QuoteReminderTemplateUpdate
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const { error: updateError } = await supabase
        .from("quote_reminder_templates")
        .update({ ...updates, updated_by: user?.id ?? null })
        .eq("id", id);

      if (updateError) throw updateError;
      return { success: true };
    } catch (err: any) {
      console.error("Erreur mise a jour template relance:", err);
      return { success: false, error: err.message || "Erreur inconnue" };
    }
  };

  return {
    templates,
    loading,
    error,
    refresh: fetchTemplates,
    updateTemplate,
  };
}
